import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Problem } from '../types';
interface ProblemDisplayProps {
  problem: Problem;
}
// Helper component to render a fraction vertically
const FractionDisplay = ({
  value,
  className = ''
}: {
  value: string;
  className?: string;
}) => {
  if (!value.includes('/')) {
    return <span className={className} data-id="element-159">{value}</span>;
  }
  const [numerator, denominator] = value.split('/');
  return <span className={`inline-flex flex-col items-center leading-none ${className}`} data-id="element-160">
      <span className="pb-1" data-id="element-161">{numerator}</span>
      <span className="border-t-2 border-slate-900 pt-1 w-full text-center" data-id="element-162">
        {denominator}
      </span>
    </span>;
};
// Helper to render a question with fractions
const renderQuestionWithFractions = (question: string) => {
  // Split by operators while keeping them
  const parts = question.split(/(\s+[\+\-×÷]\s+)/);
  return <span className="inline-flex items-center gap-4" data-id="element-163">
      {parts.map((part, index) => {
      // Check if it's an operator
      if (part.trim().match(/^[\+\-×÷]$/)) {
        return <span key={index} className="text-5xl md:text-6xl" data-id="element-164">
              {part.trim()}
            </span>;
      }
      // Check if it's a fraction
      if (part.includes('/')) {
        return <FractionDisplay key={index} value={part.trim()} className="text-5xl md:text-6xl" data-id="element-165" />;
      }
      // Regular number
      return <span key={index} data-id="element-166">{part.trim()}</span>;
    })}
    </span>;
};
export function ProblemDisplay({
  problem
}: ProblemDisplayProps) {
  const isVertical = problem.question.includes('\n');
  // Helper function to render exponents with superscript
  const renderQuestion = (text: string) => {
    // Check if it contains fractions
    if (text.includes('/')) {
      return renderQuestionWithFractions(text);
    }
    // Check if it's an exponent (contains ^)
    if (text.includes('^')) {
      const parts = text.split('^');
      return <>
          {parts[0]}
          <sup className="text-4xl md:text-5xl" data-id="element-167">{parts[1]}</sup>
        </>;
    }
    return text;
  };
  if (isVertical) {
    // Parse question for vertical display
    const lines = problem.question.split('\n');
    const operand1 = lines[0];
    const operator = lines[1];
    const operand2 = lines[2];
    // Only use vertical for standard operations
    const canBeVertical = lines.length === 3 && ['+', '-', '×', '÷'].includes(operator);
    if (canBeVertical) {
      return <div className="flex items-center justify-center py-8 min-h-[200px]" data-id="element-168">
          <AnimatePresence mode="wait" data-id="element-169">
            <motion.div key={problem.id} initial={{
            opacity: 0,
            scale: 0.9,
            y: 10
          }} animate={{
            opacity: 1,
            scale: 1,
            y: 0
          }} exit={{
            opacity: 0,
            scale: 1.1,
            y: -10
          }} transition={{
            duration: 0.2
          }} className="font-bold text-slate-900 tracking-tight" data-id="element-170">
              <div className="flex flex-col items-end text-6xl md:text-7xl font-mono" data-id="element-171">
                <div data-id="element-172">{operand1}</div>
                <div className="flex items-center gap-4 border-b-4 border-slate-900 pb-2 w-full justify-end" data-id="element-173">
                  <span className="text-4xl md:text-5xl text-slate-400" data-id="element-174">
                    {operator}
                  </span>
                  <span data-id="element-175">{operand2}</span>
                </div>
              </div>
            </motion.div>
          </AnimatePresence>
        </div>;
    }
  }
  // Horizontal display
  return <div className="flex items-center justify-center py-8 min-h-[200px]" data-id="element-176">
      <AnimatePresence mode="wait" data-id="element-177">
        <motion.div key={problem.id} initial={{
        opacity: 0,
        scale: 0.9,
        y: 10
      }} animate={{
        opacity: 1,
        scale: 1,
        y: 0
      }} exit={{
        opacity: 0,
        scale: 1.1,
        y: -10
      }} transition={{
        duration: 0.2
      }} className="font-bold text-slate-900 tracking-tight text-6xl md:text-7xl" data-id="element-178">
          {renderQuestion(problem.question)}
        </motion.div>
      </AnimatePresence>
    </div>;
}